import React, { useState, useEffect } from "react";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";
import PlayArrowIcon from "@material-ui/icons/PlayArrowOutlined";
import StopIcon from "@material-ui/icons/StopOutlined";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { useAudioPreview } from "../../utils/audioPreview";

const RATES = [
  { v: "slow", l: "Devagar" },
  { v: "normal", l: "Normal" },
  { v: "fast", l: "Rápido" },
];

// Seletor de voz unificado (fornecedor + voz + velocidade + prévia). Usado no nó
// Falar, no Menu URA e nas configurações de voz padrão do projeto.
// value: { vendor, voice, rate }. inheritLabel: texto da voz herdada (placeholder).
const VoicePicker = ({ value = {}, onChange, readOnly, previewText, inheritLabel }) => {
  const { vendor, voice, rate } = value;
  const [vendors, setVendors] = useState([]);
  const [voices, setVoices] = useState(null);
  const [loading, setLoading] = useState(false);
  const { playingUrl, toggle } = useAudioPreview();
  const vend = vendor || vendors[0] || "openai";

  useEffect(() => {
    api.get("/tts/vendors").then(({ data }) => setVendors(data.vendors || ["openai"])).catch(() => setVendors(["openai"]));
  }, []);

  useEffect(() => {
    setVoices(null);
    api.get("/tts/voices", { params: { vendor: vend } })
      .then(({ data }) => setVoices(data.voices || []))
      .catch(() => setVoices([]));
  }, [vend]);

  const set = patch => onChange({ vendor, voice, rate, ...patch });

  // Blinda o Select: o value SEMPRE precisa estar entre as opções, senão o MUI v4
  // fica "out-of-range" e quebra a tela (ex.: nó salvo com um fornecedor/voz que
  // não está mais disponível na conta). Então incluímos o valor atual como opção.
  const vendorOpts = vendors.length ? vendors : ["openai"];
  const allVendorOpts = vendorOpts.includes(vend) ? vendorOpts : [vend, ...vendorOpts];
  const voiceList = (voices || []).filter(v => v && v.id != null);
  const voiceMissing = voice && !voiceList.some(v => String(v.id) === String(voice));

  const preview = async () => {
    try {
      setLoading(true);
      const { data } = await api.post("/tts/preview", {
        text: previewText || "Olá, esta é uma demonstração da voz.",
        voice: voice || undefined,
        vendor: vend,
        rate: rate || "normal",
      });
      if (data.url) toggle(data.url);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const playing = playingUrl && loading === false;

  return (
    <div>
      <div style={{ display: "flex", gap: 8 }}>
        <TextField
          select label="Fornecedor" size="small" variant="outlined" fullWidth disabled={readOnly}
          value={vend} onChange={e => set({ vendor: e.target.value, voice: "" })}
        >
          {allVendorOpts.map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
        </TextField>
        <TextField
          select label="Velocidade" size="small" variant="outlined" style={{ width: 150 }} disabled={readOnly}
          value={rate || "normal"} onChange={e => set({ rate: e.target.value })}
        >
          {RATES.map(r => <MenuItem key={r.v} value={r.v}>{r.l}</MenuItem>)}
        </TextField>
      </div>
      <TextField
        select label="Voz" size="small" variant="outlined" fullWidth disabled={readOnly} style={{ marginTop: 10 }}
        value={voice || ""} onChange={e => set({ voice: e.target.value })}
      >
        {inheritLabel && <MenuItem value="">{`Herdar do projeto${inheritLabel ? ` (${inheritLabel})` : ""}`}</MenuItem>}
        {!inheritLabel && <MenuItem value="">Padrão</MenuItem>}
        {voiceMissing && <MenuItem value={voice}>{`${voice} (indisponível)`}</MenuItem>}
        {voiceList.map(v => <MenuItem key={v.id} value={v.id}>{v.name || v.id}</MenuItem>)}
      </TextField>
      {voices === null && <Typography style={{ fontSize: 12, color: "#929AA4", marginTop: 6 }}>Carregando vozes…</Typography>}
      {!readOnly && (
        <Button
          size="small"
          onClick={preview}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={14} /> : playing ? <StopIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
          style={{ marginTop: 8, textTransform: "none" }}
        >
          Ouvir prévia
        </Button>
      )}
    </div>
  );
};

export default VoicePicker;
